// Clean, matrix-style price list for the prospect page: core products + packages, then bolt-ons.
import { Fragment } from "react";
import { Eye, EyeOff } from "lucide-react";
import { sqftNote } from "./roiMath";

const fmt = (n) => {
  if (n === null || n === undefined || n === "") return "—";
  const num = Number(n);
  return Number.isNaN(num) ? n : `£${num.toLocaleString("en-GB")}`;
};

// "Property Photographs (up to 20 photos)" — count is per-proposal editable
export const priceListName = (sp) => {
  const n = sp.photo_count ?? sp.product?.photo_count;
  if (n == null || n === "") return sp.product.name;
  const prefix = sp.product?.photo_count_prefix ? `${sp.product.photo_count_prefix} ` : "";
  return `${sp.product.name} (${prefix}${n} ${Number(n) === 1 ? "photo" : "photos"})`;
};

// Bolt-on price = stand-alone price minus the discount (£30 by default) when added to an existing visit
export const boltOnPrice = (sp) => {
  const d = sp.bolt_on_discount ?? sp.product?.bolt_on_discount;
  if (d == null || d === "") return null;
  return Math.max(0, Number(sp.nichecom_price) - Number(d));
};

const isBoltOnGroup = (sp) => (sp.product?.price_group ? sp.product.price_group === "bolt_on" : boltOnPrice(sp) != null || !!sp.product?.addon_only);

export default function ProposalPriceList({ proposal, orderedProducts, orderedBundles, smallPrint, revealVendor, onToggleReveal, hasAnyVendorPricing }) {
  const core = orderedProducts.filter((sp) => !isBoltOnGroup(sp));
  const bolt = orderedProducts.filter(isBoltOnGroup);
  const hasBolt = bolt.some((sp) => boltOnPrice(sp) != null);
  const note = sqftNote(proposal.roi);

  const Row = ({ name, price, bolt: boltPrice, resale, testId, kind, boltCol }) => (
    <tr className={`npl-row ${kind || ""}`} data-testid={testId}>
      <td className="npl-name">{name}</td>
      <td className="npl-price">{fmt(price)}</td>
      {hasBolt && <td className="npl-bolt">{boltPrice != null ? fmt(boltPrice) : boltCol ? <span className="npl-dash">–</span> : null}</td>}
      {revealVendor && <td className="npl-resale">{resale ? fmt(resale) : <span className="npl-dash">–</span>}</td>}
    </tr>
  );

  const Head = ({ title, boltLabel }) => (
    <tr className="npl-head">
      <th>{title}</th>
      <th className="npl-price">Price</th>
      {hasBolt && <th className="npl-bolt">{boltLabel || ""}</th>}
      {revealVendor && <th className="npl-resale">Your resale</th>}
    </tr>
  );

  return (
    <section className="nbp-table-section npl" key="pricing" data-testid="nbp-block-pricing">
      <div className="nbp-section-head">
        <span className="nbp-eyebrow-dark">Price list</span>
        <h2 className="nbp-h2">{new Date().getFullYear()} prices for {proposal.agency_name}</h2>
        <p className="nbp-section-sub">Simple per-property pricing — pick any combination, any time.</p>
      </div>

      {hasAnyVendorPricing && (
        <div className="nbp-reveal">
          <button className={`nbp-reveal-btn ${revealVendor ? "is-on" : ""}`} onClick={onToggleReveal} data-testid="nbp-reveal-toggle">
            {revealVendor ? <EyeOff size={14} /> : <Eye size={14} />}
            {revealVendor ? "Hide your resale pricing" : "Reveal your resale pricing"}
          </button>
        </div>
      )}

      <div className="npl-card" data-testid="nbp-pricing-table">
        <table className="npl-table">
          <thead><Head title="Core products and packages" /></thead>
          <tbody>
            {core.map((sp) => (
              <Row key={sp.product_id} name={priceListName(sp)} price={sp.nichecom_price} resale={sp.vendor_price} testId={`npl-core-${sp.product_id}`} />
            ))}
            {core.length > 0 && orderedBundles.length > 0 && (
              <tr className="npl-gap"><td colSpan={2 + (hasBolt ? 1 : 0) + (revealVendor ? 1 : 0)}><span className="npl-gap-label">Packages</span></td></tr>
            )}
            {orderedBundles.map((b, i) => (
              <Fragment key={b.id || i}>
                <Row kind="is-package" name={b.name || `Package ${i + 1}`} price={b.price_without_epc} resale={b.vendor_price_without_epc} testId={`npl-bundle-${b.id || i}`} />
                {b.includes_epc_option && b.price_with_epc != null && (
                  <Row kind="is-package" name={`${b.name || `Package ${i + 1}`} + EPC`} price={b.price_with_epc} resale={b.vendor_price_with_epc} testId={`npl-bundle-${b.id || i}-epc`} />
                )}
              </Fragment>
            ))}
          </tbody>
          {bolt.length > 0 && (
            <>
              <thead><Head title="Bolt-on products" boltLabel="Bolt-on price*" /></thead>
              <tbody>
                {bolt.map((sp) => (
                  <Row key={sp.product_id} boltCol name={priceListName(sp)} price={sp.nichecom_price} bolt={boltOnPrice(sp)} resale={sp.vendor_price} testId={`npl-bolt-${sp.product_id}`} />
                ))}
              </tbody>
            </>
          )}
        </table>
        <div className="npl-foot" data-testid="nbp-small-print">
          {hasBolt && <p>* Bolt-on price applies when added to an existing NicheCom visit.</p>}
          {note && <p>{note}.</p>}
          {smallPrint && <p>{smallPrint}</p>}
          <p>All prices quoted are subject to VAT at the prevailing rate.</p>
        </div>
      </div>
    </section>
  );
}
